/**
 * AddEditRelationshipDialog
 * Dialog for adding a relationship between two ecosystem entities.
 */

import { useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAddRelationship, useEcosystemOrganizations } from '@/hooks/useEcosystemData';
import {
  RELATIONSHIP_TYPE_LABELS,
  type EntityRelationshipType,
} from '@/types/ecosystem';

interface AddEditRelationshipDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityId: string;
  entityName: string;
}

export function AddEditRelationshipDialog({
  open,
  onOpenChange,
  entityId,
  entityName,
}: AddEditRelationshipDialogProps) {
  const [targetEntityId, setTargetEntityId] = useState('');
  const [relationshipType, setRelationshipType] = useState<EntityRelationshipType>('belongs_to');
  const [direction, setDirection] = useState<'parent' | 'child'>('parent');
  const [isPrimary, setIsPrimary] = useState(true);

  const addRelationship = useAddRelationship();
  const { data: allEntities } = useEcosystemOrganizations();

  const entityOptions = (allEntities || []).filter((e: any) => e.id !== entityId);

  const handleSubmit = async () => {
    if (!targetEntityId) {
      toast.error('Please select a target entity');
      return;
    }

    const parentId = direction === 'parent' ? targetEntityId : entityId;
    const childId = direction === 'parent' ? entityId : targetEntityId;

    try {
      await addRelationship.mutateAsync({
        data: {
          parent_id: parentId,
          child_id: childId,
          relationship_type: relationshipType,
          is_primary: isPrimary,
        },
      });
      toast.success('Relationship added');
      onOpenChange(false);
      setTargetEntityId('');
      setRelationshipType('belongs_to');
      setDirection('parent');
      setIsPrimary(true);
    } catch (error: any) {
      toast.error(error.message || 'Failed to add relationship');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle>Add Relationship</DialogTitle>
          <DialogDescription>
            Create a relationship for <strong>{entityName}</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label>Direction</Label>
            <Select value={direction} onValueChange={(v) => setDirection(v as 'parent' | 'child')}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="parent">This entity is child of…</SelectItem>
                <SelectItem value="child">This entity is parent of…</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label>Target Entity</Label>
            <Select value={targetEntityId} onValueChange={setTargetEntityId}>
              <SelectTrigger><SelectValue placeholder="Select entity…" /></SelectTrigger>
              <SelectContent>
                {entityOptions.map((e: any) => (
                  <SelectItem key={e.id} value={e.id}>{e.display_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label>Relationship Type</Label>
            <Select value={relationshipType} onValueChange={(v) => setRelationshipType(v as EntityRelationshipType)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {(Object.entries(RELATIONSHIP_TYPE_LABELS) as [EntityRelationshipType, string][]).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="isPrimary">Primary placement</Label>
            <Switch id="isPrimary" checked={isPrimary} onCheckedChange={setIsPrimary} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={addRelationship.isPending}>
            {addRelationship.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Add Relationship
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
